import { ACCOUNT_REFUSAL_CODES, type AccountRefusalCode } from '@nestjs-boilerplate/types';

import { clearAuthState, getAuthState, setAuthState } from '@/stores/auth.store';

const API_BASE = '/api';

interface ApiError {
  message: string;
  statusCode: number;
  /** Present only on the account-state refusals — see AccountRefusalCode. */
  code?: string;
}

function isAccountRefusal(code: string | undefined): code is AccountRefusalCode {
  return code !== undefined && (ACCOUNT_REFUSAL_CODES as readonly string[]).includes(code);
}

class ApiClient {
  private refreshPromise: Promise<boolean> | null = null;

  async fetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const { accessToken } = getAuthState();

    const headers: HeadersInit = {
      ...options.headers,
    };

    // Only set Content-Type for requests with a body
    if (options.body) {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    if (accessToken) {
      (headers as Record<string, string>).Authorization = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include', // Include cookies for refresh token
    });

    // Handle 401 - try refresh
    if (response.status === 401 && accessToken) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        // Retry original request with new token
        return this.fetch<T>(endpoint, options);
      }
      // Refresh failed - clear auth and throw
      clearAuthState();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        message: 'An error occurred',
        statusCode: response.status,
      }));

      // A disabled, rejected or deleted account keeps a valid token until it
      // expires, and the API refuses it on every request from here on. Without
      // this the user sits on the page collecting error toasts, still
      // apparently signed in, because clearAuthState alone does not navigate
      // and the route guard only re-runs on navigation.
      //
      // Gated on the code, never the status: 403 also means "not your
      // assessment" and "missing permission", and signing an administrator out
      // for opening a page they lack a permission for would be its own bug.
      if (response.status === 403 && isAccountRefusal(error.code)) {
        clearAuthState();
        // A full load, not a router navigate: this module has no router
        // access, and replacing the document also drops any cached state
        // belonging to an account that just lost its access.
        window.location.replace('/login');
      }

      throw new Error(error.message);
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  private async tryRefresh(): Promise<boolean> {
    // Deduplicate concurrent refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performRefresh();
    const result = await this.refreshPromise;
    this.refreshPromise = null;
    return result;
  }

  private async performRefresh(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      const { accessToken } = data;
      const { user } = getAuthState();

      if (accessToken && user) {
        setAuthState(accessToken, user);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }
}

export const apiClient = new ApiClient();
